/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { getEarlyScriptObservabilityCache } from './getEarlyScriptObservabilityCache.tsx';

/**
 * Waits for a global variable to become available on the window object.
 * Uses a combination of requestAnimationFrame and setTimeout for efficient polling.
 *
 * @example
 * ```typescript
 * // Wait for myVar to be available
 * const myVar = await waitForGlobalVariable('myVar');
 *
 * // Wait for a custom variable with timeout
 * try {
 *   const myVar = await waitForGlobalVariable('myVar', 10000);
 *   console.log('App loaded:', myVar);
 * } catch (error) {
 *   console.error('App failed to load within timeout');
 * }
 * ```
 *
 * @param varName - The name of the global variable to wait for
 * @param timeout - Maximum time to wait in milliseconds (default: 5000ms)
 * @returns Promise that resolves with the variable value or rejects on timeout
 * @throws Error when variable is not found within the timeout period
 */
export const waitForGlobalVariable = <T = unknown,>(
	varName: string,
	timeout = 2000,
): Promise<T> => {
	const start = performance.now();
	let promise = new Promise<T>((resolve, reject) => {
		let rafId: number | undefined;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		let completed = false;

		/**
		 * Cleanup function to cancel animation frames and clear timeouts
		 */
		const cleanup = (): void => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};

		/**
		 * Check if the variable is available on the window object
		 */
		const check = (): void => {
			// Check if variable exists and is not undefined
			const windowObj = globalThis?.window;
			if (windowObj && Reflect.has(windowObj, varName)) {
				const value = Reflect.get(windowObj, varName);
				if (value !== undefined) {
					completed = true;
					cleanup();
					resolve(value);
					return;
				}
			}

			// Continue polling using requestAnimationFrame
			rafId = requestAnimationFrame(check);
		};

		// Set up timeout to reject if variable is not found
		timeoutId = setTimeout(() => {
			if (!completed) {
				completed = true;
				cleanup();
				reject(new Error(`Variable ${varName} not found within ${timeout}ms`));
			}
		}, timeout);

		// Start checking for the variable
		check();
	});

	promise = promise.then((value) => {
		getEarlyScriptObservabilityCache()?.recordMetric(
			'custom',
			'global-variable-polling',
			[{ name: 'jpdApp' }, { name: 'earlyScript' }],
			start,
			performance.now(),
		);
		return value;
	});

	return promise;
};
